/**
 * Draft figures for the Beilage E1a (income from a business, Einnahmen-Ausgaben-Rechnung): the
 * year's income statement sorted into the form's Kennzahlen, the Arbeitsplatzpauschale, the
 * Gewinnfreibetrag, and the Basispauschalierung as the alternative to compare with.
 *
 * Kennzahlen follow the E1a 2025 (version of 24.10.2025) and its Ausfüllhilfe E2; the BMF has not
 * published a 2026 form yet. Amounts are what may be deducted (Bewirtung at 50 %, the private
 * share of an asset left out), so the corrections section of the form stays empty. Rates and
 * limits were checked in RIS on 31 Oct 2026:
 *
 *  - **Gewinnfreibetrag** (§ 10 EStG, from 2024): 15 % of the first 33.000 € without investment
 *    (Grundfreibetrag, 9221); above that 13 % / 7 % / 4,5 % on the next 145.000 / 175.000 /
 *    230.000 €, at most what was invested in new depreciable assets of at least four years' life,
 *    cars and GWG excluded (9227). Securities (9229) count until 2026 and are not tracked here.
 *  - **Arbeitsplatzpauschale** (§ 4 Abs 4 Z 8 EStG, 2022 to 2026): 1.200 € (9217) without other
 *    income above the threshold from work with a room of its own, 300 € (9215) otherwise. The
 *    Budgetbegleitgesetz 2027-2028 abolishes it for years starting after 31 Dec 2026.
 *  - **Basispauschalierung** (§ 17 EStG): 12 % until 2024, 13,5 % in 2025, 15 % from 2026 (6 % for
 *    consulting, writing, lecturing and teaching), each with its maximum, if the previous year's
 *    turnover stayed under 220.000 / 320.000 / 420.000 €. Goods, own and outside staff, SVS and
 *    the Arbeitsplatzpauschale are deducted on top; the Gewinnfreibetrag is the Grundfreibetrag.
 *
 * The Investitionsfreibetrag (§ 11 EStG) is not drafted: it competes with the Gewinnfreibetrag for
 * the same asset and needs the eco flag the asset register does not have.
 */
import { afaSchedule, isGwg, type AssetInput } from "./afa";
import type { IncomeStatement, StatementRow } from "./uva";

export const E1A_CODES = [
  "9040", // Erträge/Betriebseinnahmen
  "9090", // Übrige Erträge
  "9100", // Waren, Rohstoffe, Hilfsstoffe
  "9110", // Fremdpersonal und Fremdleistungen
  "9120", // Personalaufwand
  "9130", // AfA auf das Anlagevermögen (inkl. GWG)
  "9134", // Degressive AfA (§ 7 Abs 1a)
  "9160", // Reise- und Fahrtspesen inkl. Kilometergeld und Diäten
  "9170", // Tatsächliche Kfz-Kosten
  "9180", // Miet- und Pachtaufwand, Leasing
  "9190", // Provisionen an Dritte, Lizenzgebühren
  "9200", // Werbe- und Repräsentationsaufwendungen
  "9210", // Buchwert abgegangener Anlagen
  "9215", // Kleines Arbeitsplatzpauschale
  "9217", // Großes Arbeitsplatzpauschale
  "9220", // Zinsen und ähnliche Aufwendungen
  "9225", // Eigene Pflichtversicherungsbeiträge (SVS)
  "9230", // Übrige Aufwendungen
  "9221", // Grundfreibetrag
  "9227", // Investitionsbedingter Gewinnfreibetrag
] as const;
export type E1aCode = (typeof E1A_CODES)[number];

export const REVENUE_CODES: E1aCode[] = ["9040", "9090"];
export const EXPENSE_CODES: E1aCode[] = [
  "9100",
  "9110",
  "9120",
  "9130",
  "9134",
  "9160",
  "9170",
  "9180",
  "9190",
  "9200",
  "9210",
  "9215",
  "9217",
  "9220",
  "9225",
  "9230",
];

/**
 * The Kennzahl a category's amounts go to, from its account number in the Einheitskontenrahmen
 * and the default chart (server/pb_hooks/lib/categories.js). Anything unknown is "übrige".
 */
export function e1aCodeFor(category: { kind: "revenue" | "expense"; accountNumber?: string }) {
  const number = Number((category.accountNumber ?? "").trim());
  if (category.kind === "revenue") return number >= 4000 && number < 4600 ? "9040" : "9090";
  if (!Number.isInteger(number) || number <= 0) return "9230";
  const ranges: Array<[number, number, E1aCode]> = [
    [5000, 5699, "9100"],
    [5700, 5999, "9110"],
    [6000, 6999, "9120"],
    [7000, 7099, "9130"],
    [7100, 7199, "9180"],
    [7330, 7349, "9160"],
    [7400, 7499, "9180"],
    [7500, 7599, "9190"],
    [7650, 7689, "9200"],
    [7710, 7710, "9225"],
    [7820, 7820, "9210"],
    [7800, 7899, "9170"],
    [8000, 8499, "9220"],
  ];
  return ranges.find(([from, to]) => number >= from && number <= to)?.[2] ?? "9230";
}

export type Workplace = "none" | "small" | "large";

/** The Arbeitsplatzpauschale in euros: from 2022 until it was abolished after 2026. */
export function workplaceAmount(choice: Workplace, year: number): number {
  if (year < 2022 || year > 2026 || choice === "none") return 0;
  return choice === "large" ? 1200 : 300;
}

/** Other active income above which only the small pauschale applies (§ 124b EStG). */
export function workplaceThreshold(year: number): number {
  return year >= 2026 ? 13_539 : year === 2025 ? 13_308 : 11_000;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/** 15 % of the first 33.000 € of profit, without any investment (§ 10 Abs 1 Z 1 EStG). */
export function basicAllowance(profit: number): number {
  return round2(Math.min(Math.max(profit, 0), 33_000) * 0.15);
}

/** The investment-linked Gewinnfreibetrag the profit above 33.000 € allows, before its cover. */
export function investmentAllowanceLimit(profit: number): number {
  let rest = Math.max(profit - 33_000, 0);
  let total = 0;
  for (const [band, rate] of [
    [145_000, 0.13],
    [175_000, 0.07],
    [230_000, 0.045],
  ] as const) {
    const part = Math.min(rest, band);
    total += part * rate;
    rest -= part;
  }
  return round2(total);
}

/** New depreciable assets that can cover it: at least four years' life, no GWG, no car. */
export function coveringInvestments(assets: AssetInput[], year: number): number {
  return round2(
    assets
      .filter(
        (asset) =>
          asset.inServiceDate.startsWith(String(year)) &&
          asset.type !== "car" &&
          !isGwg(asset) &&
          (asset.usefulLifeYears ?? 0) >= 4,
      )
      .reduce((sum, asset) => sum + asset.cost, 0),
  );
}

/** Basispauschalierung of a year: rate, maximum and the previous year's turnover limit. */
export function flatRate(year: number, reduced: boolean) {
  if (year >= 2026)
    return reduced
      ? { rate: 0.06, max: 25_200, limit: 420_000 }
      : { rate: 0.15, max: 63_000, limit: 420_000 };
  if (year === 2025)
    return reduced
      ? { rate: 0.06, max: 19_200, limit: 320_000 }
      : { rate: 0.135, max: 43_200, limit: 320_000 };
  return reduced
    ? { rate: 0.06, max: 13_200, limit: 220_000 }
    : { rate: 0.12, max: 26_400, limit: 220_000 };
}

export type E1aInput = {
  year: number;
  /** The year's income statement without the AfA (it is split by method here). */
  statement: IncomeStatement;
  categories: Array<{ id: string; kind: "revenue" | "expense"; accountNumber?: string }>;
  assets: AssetInput[];
  workplace: Workplace;
  /** 6 % instead of the standard rate for the Basispauschalierung. */
  reducedFlatRate: boolean;
  /** Net turnover of the year before, for the Basispauschalierung's limit. */
  previousTurnover: number;
};

export type E1aDraft = {
  amounts: Record<E1aCode, number>;
  /** Where each row of the income statement went, so the person can check the sorting. */
  sources: Array<{ code: E1aCode; name: string; accountNumber?: string; amount: number }>;
  revenue: number;
  expenses: number;
  /** Before the Gewinnfreibetrag. */
  profitBefore: number;
  allowance: { basic: number; investment: number; limit: number; cover: number };
  profit: number;
  flat: {
    allowed: boolean;
    rate: number;
    expenses: number;
    /** Deducted on top of the flat rate: goods, staff, SVS, Arbeitsplatzpauschale. */
    extra: number;
    allowance: number;
    profit: number;
  };
};

export function computeE1a(input: E1aInput): E1aDraft {
  const amounts = Object.fromEntries(E1A_CODES.map((code) => [code, 0])) as Record<E1aCode, number>;
  const sources: E1aDraft["sources"] = [];
  const categories = new Map(input.categories.map((category) => [category.id, category]));
  const add = (code: E1aCode, value: number) => {
    amounts[code] = round2(amounts[code] + value);
  };

  const place = (row: StatementRow, kind: "revenue" | "expense", value: number) => {
    const category = categories.get(row.categoryId);
    const code: E1aCode =
      row.categoryId === "travel"
        ? "9160"
        : e1aCodeFor({ kind, accountNumber: category?.accountNumber ?? row.accountNumber });
    add(code, value);
    sources.push({ code, name: row.name, accountNumber: row.accountNumber, amount: value });
  };
  for (const row of input.statement.revenue) place(row, "revenue", row.amount);
  for (const row of input.statement.expenses) place(row, "expense", row.deductible);

  // AfA by method, and what is left of an asset given up; never the private share.
  for (const asset of input.assets) {
    const row = afaSchedule(asset).find((entry) => entry.year === input.year);
    if (!row) continue;
    const share = 1 - Math.min(100, Math.max(0, asset.privateUsePercent ?? 0)) / 100;
    const depreciation = Math.round(row.depreciation * share);
    add(asset.method === "degressive" ? "9134" : "9130", depreciation / 100);
    add("9210", (row.deductible - depreciation) / 100);
  }
  for (const code of ["9130", "9134", "9210"] as const) {
    if (amounts[code] !== 0) sources.push({ code, name: "", amount: amounts[code] });
  }

  const workplace = workplaceAmount(input.workplace, input.year);
  if (workplace) add(input.workplace === "large" ? "9217" : "9215", workplace);

  const revenue = round2(REVENUE_CODES.reduce((sum, code) => sum + amounts[code], 0));
  const expenses = round2(EXPENSE_CODES.reduce((sum, code) => sum + amounts[code], 0));
  const profitBefore = round2(revenue - expenses);

  const basic = basicAllowance(profitBefore);
  const limit = investmentAllowanceLimit(profitBefore);
  const cover = coveringInvestments(input.assets, input.year);
  const investment = round2(Math.min(limit, cover));
  amounts["9221"] = basic;
  amounts["9227"] = investment;

  const terms = flatRate(input.year, input.reducedFlatRate);
  const flatExpenses = round2(Math.min(revenue * terms.rate, terms.max));
  const extra = round2(
    (["9100", "9110", "9120", "9225"] as const).reduce((sum, code) => sum + amounts[code], 0) +
      workplace,
  );
  const flatBefore = round2(revenue - flatExpenses - extra);
  const flatAllowance = basicAllowance(flatBefore);

  return {
    amounts,
    sources,
    revenue,
    expenses,
    profitBefore,
    allowance: { basic, investment, limit, cover },
    profit: round2(profitBefore - basic - investment),
    flat: {
      allowed: input.previousTurnover <= terms.limit,
      rate: terms.rate,
      expenses: flatExpenses,
      extra,
      allowance: flatAllowance,
      profit: round2(flatBefore - flatAllowance),
    },
  };
}
